import { useState, useEffect, useContext } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  Video,
  ExternalLink,
  CheckCircle2
} from "lucide-react"
import { useWebinars } from "../hooks/useWebinars"
import { useAuth } from "../hooks/useAuth"
import { Button } from "../components/ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../components/Card"
import { Badge } from "../components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar"
import { formatDate, formatTime } from "../utils/formatters"
import { useTranslation } from "../i18n"
import { TranslationContext } from "../i18n/TranslationContext"

export default function WebinarDetailPage() {
  const { webinarId } = useParams()
  const navigate = useNavigate()
  const { tObj } = useTranslation()
  const translationContext = useContext(TranslationContext)
  const userLanguage = translationContext?.language || 'fr'
  const { isAuthenticated } = useAuth()
  const { getWebinar, registerToWebinar, cancelRegistration, loading } = useWebinars()
  const [webinar, setWebinar] = useState(null)
  const [isRegistering, setIsRegistering] = useState(false)

  useEffect(() => {
    const loadWebinar = async () => {
      try {
        // Récupérer le webinaire avec la langue de l'utilisateur
        const data = await getWebinar(webinarId, { language: userLanguage })
        setWebinar(data)
      } catch (error) {
        console.error("Erreur lors du chargement du webinaire:", error)
      }
    }
    loadWebinar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinarId, userLanguage])

  const handleRegister = async () => {
    if (!isAuthenticated) {
      navigate("/login")
      return
    }

    setIsRegistering(true)
    try {
      await registerToWebinar(webinarId)
      // Recharger les détails pour mettre à jour isRegistered
      const data = await getWebinar(webinarId)
      setWebinar(data)
    } catch {
      // L'erreur est déjà gérée dans le hook
    } finally {
      setIsRegistering(false)
    }
  }

  const handleCancelRegistration = async () => {
    setIsRegistering(true)
    try {
      await cancelRegistration(webinarId)
      // Recharger les détails pour mettre à jour isRegistered
      const data = await getWebinar(webinarId)
      setWebinar(data)
    } catch {
      // L'erreur est déjà gérée dans le hook
    } finally {
      setIsRegistering(false)
    }
  }

  const getStatusBadge = () => {
    switch (webinar?.status) {
      case 'UPCOMING':
        return { text: tObj.webinars?.status?.upcoming?.() || 'À venir', className: 'bg-blue-500 text-white' }
      case 'ONGOING':
        return { text: tObj.webinars?.status?.ongoing?.() || 'En cours', className: 'bg-green-500 text-white' }
      case 'COMPLETED':
        return { text: tObj.webinars?.status?.completed?.() || 'Terminé', className: 'bg-gray-500 text-white' }
      default:
        return { text: tObj.webinars?.status?.unknown?.() || 'Non défini', className: 'bg-gray-400 text-white' }
    }
  }

  if (loading && !webinar) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <p className="text-gray-500">{tObj.common?.loading?.() || "Chargement..."}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!webinar) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {tObj.webinars?.notFound?.() || "Webinaire introuvable"}
            </h2>
            <p className="text-gray-600 mb-4">
              {tObj.webinars?.notFoundDesc?.() || "Le webinaire demandé n'existe pas ou n'est plus disponible."}
            </p>
            <Button asChild>
              <Link to="/webinars">
                {tObj.webinars?.backToList?.() || "Retour aux webinaires"}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const statusBadge = getStatusBadge()
  const webinarDate = webinar?.date ? new Date(webinar.date) : null
  const isUpcoming = webinar?.status === 'UPCOMING'
  const isOngoing = webinar?.status === 'ONGOING'
  const isCompleted = webinar?.status === 'COMPLETED'
  const canRegister = isAuthenticated && isUpcoming && webinar?.isPublished && !webinar?.isRegistered
  const canCancel = isAuthenticated && isUpcoming && webinar?.isRegistered

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header amélioré */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-30">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/webinars")}
            className="text-sm sm:text-base text-gray-600 hover:text-[#203a8a] transition-all duration-300 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
            {tObj.common?.back?.() || "Retour"}
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 max-w-4xl py-6 sm:py-8">
        {/* Image de couverture améliorée */}
        {webinar.thumbnail && (
          <div className="mb-6 sm:mb-8 rounded-2xl overflow-hidden shadow-2xl group hover:shadow-[0_0_50px_rgba(31,58,138,0.3)] transition-all duration-500">
            <div className="relative">
              <img
                src={webinar.thumbnail}
                alt={webinar.title}
                className="w-full h-64 sm:h-80 object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            </div>
          </div>
        )}

        {/* En-tête amélioré */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge className={`${statusBadge.className} text-xs sm:text-sm shadow-lg hover:scale-110 transition-transform duration-300`}>
              {statusBadge.text}
            </Badge>
            {!webinar.isPublished && (
              <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-xs sm:text-sm shadow-lg hover:scale-110 transition-transform duration-300">
                {tObj.webinars?.draft?.() || "Brouillon"}
              </Badge>
            )}
            {isAuthenticated && webinar.isRegistered && (
              <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center gap-1 text-xs sm:text-sm shadow-lg hover:scale-110 transition-transform duration-300">
                <CheckCircle2 className="w-3 h-3" />
                {tObj.webinars?.registered?.() || "Inscrit"}
              </Badge>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent mb-4 animate-gradient">
            {webinar.title}
          </h1>
        </div>

        {/* Informations principales améliorées */}
        <Card className="mb-6 border-0 shadow-xl bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300">
          <CardHeader className="bg-gradient-to-r from-[#203a8a]/10 to-purple-50/50 rounded-t-xl">
            <CardTitle className="bg-gradient-to-r from-[#141835] to-[#203a8a] bg-clip-text text-transparent">
              {tObj.webinars?.details?.title?.() || "Informations"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date et heure */}
            {webinarDate && (
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="font-medium">{formatDate(webinarDate)}</p>
                  <p className="text-sm text-gray-600">{formatTime(webinarDate)}</p>
                </div>
              </div>
            )}

            {/* Durée */}
            {webinar.duration && (
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-gray-500" />
                <span>{webinar.duration} {tObj.webinars?.details?.minutes?.() || "minutes"}</span>
              </div>
            )}

            {/* Intervenants */}
            {webinar.speakers && Array.isArray(webinar.speakers) && webinar.speakers.length > 0 && (
              <div className="pt-2 border-t">
                <h3 className="font-medium mb-3">Intervenants</h3>
                <div className="space-y-3">
                  {webinar.speakers.map((speaker) => (
                    <div key={speaker.id} className="flex items-center space-x-3">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={speaker.photo} alt={speaker.name} />
                        <AvatarFallback>
                          {speaker.name
                            ? speaker.name.split(" ").map((n) => n[0]).join("")
                            : "SP"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium">{speaker.name}</p>
                        {speaker.title && (
                          <p className="text-sm text-gray-600">{speaker.title}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </CardContent>
        </Card>

        {/* Description améliorée */}
        <Card className="mb-6 border-0 shadow-xl bg-white/90 backdrop-blur-sm hover:shadow-2xl transition-all duration-300">
          <CardHeader className="bg-gradient-to-r from-[#203a8a]/10 to-purple-50/50 rounded-t-xl">
            <CardTitle className="bg-gradient-to-r from-[#141835] to-[#203a8a] bg-clip-text text-transparent">
              {tObj.webinars?.details?.description?.() || "Description"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-base">
              {webinar.description || tObj.webinars?.noDescription?.() || "Aucune description disponible."}
            </p>
          </CardContent>
        </Card>

        {/* Actions améliorées */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Bouton inscription/annulation */}
          {isUpcoming && webinar.isPublished && (
            <>
              {canRegister && (
                <Button
                  onClick={handleRegister}
                  disabled={isRegistering}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                  size="lg"
                >
                  {isRegistering
                    ? tObj.webinars?.registering?.() || "Inscription..."
                    : tObj.webinars?.register?.() || "S'inscrire au webinaire"}
                </Button>
              )}
              {canCancel && (
                <Button
                  onClick={handleCancelRegistration}
                  disabled={isRegistering}
                  variant="outline"
                  className="flex-1 border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 hover:scale-105 transition-all duration-300"
                  size="lg"
                >
                  {isRegistering
                    ? tObj.webinars?.cancelling?.() || "Annulation..."
                    : tObj.webinars?.cancelRegistration?.() || "Annuler l'inscription"}
                </Button>
              )}
              {!isAuthenticated && !webinar?.isRegistered && (
                <Button
                  onClick={() => navigate("/login")}
                  className="flex-1 bg-[#203a8a] text-white hover:bg-[#1a1f4a]"
                  size="lg"
                >
                  {tObj.webinars?.loginToRegister?.() || "Se connecter pour s'inscrire"}
                </Button>
              )}
            </>
          )}

          {/* Lien de la réunion - toujours visible si l'événement est en cours */}
          {isOngoing && webinar.meetLink && (
            <Button
              asChild
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
              size="lg"
            >
              <a
                href={webinar.meetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {tObj.webinars?.joinMeeting?.() || "Rejoindre la réunion"}
              </a>
            </Button>
          )}
          
          {/* Lien de la réunion si à venir et inscrit */}
          {isUpcoming && isAuthenticated && webinar?.isRegistered && webinar.meetLink && (
            <Button
              asChild
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
              size="lg"
            >
              <a
                href={webinar.meetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {tObj.webinars?.joinMeeting?.() || "Rejoindre la réunion"}
              </a>
            </Button>
          )}

          {/* Lien vidéo replay si terminé */}
          {isCompleted && webinar.videoLink && (
            <Button
              asChild
              className="flex-1 bg-red-600 text-white hover:bg-red-700"
              size="lg"
            >
              <a
                href={webinar.videoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2"
              >
                <Video className="w-4 h-4" />
                {tObj.webinars?.watchReplay?.() || "Voir le replay"}
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

